import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/Header";
import InfoTip from "@/components/InfoTip";
import { api, downloadAuthed } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { ArrowLeft, FileDown, Loader2, Bell, BellOff, Eye, X } from "lucide-react";
import { toast } from "sonner";

const SOPH_LABEL_KEY = {
  basico: "soph_basico",
  intermedio: "soph_intermedio",
  avanzado: "soph_avanzado",
  experto: "soph_experto",
};

function Bar({ value, max, label, count, accent = false }) {
  const pct = max > 0 ? Math.max(4, (value / max) * 100) : 0;
  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-3 py-2">
      <div>
        <div className="font-mono-tech text-[10px] uppercase tracking-widest mb-1 flex justify-between">
          <span>{label}</span>
          <span className="text-[#A3A3A3]">{count}</span>
        </div>
        <div className="h-2 bg-[#F5F5F5] border border-[#E5E5E5]">
          <div
            className={`h-full ${accent ? "bg-[#D90429]" : "bg-[#0A0A0A]"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function Timeline({ data }) {
  const max = useMemo(() => data.reduce((m, x) => Math.max(m, x.count), 0), [data]);
  if (data.length === 0) {
    return <div className="font-mono-tech text-xs uppercase text-[#A3A3A3]">—</div>;
  }
  return (
    <div className="flex items-end gap-1 h-32 border-b border-[#E5E5E5] pb-1" data-testid="timeline">
      {data.map((p) => {
        const h = max > 0 ? Math.max(8, (p.count / max) * 100) : 4;
        return (
          <div key={p.week} className="flex-1 flex flex-col items-center group">
            <div
              className="w-full bg-[#0A0A0A] group-hover:bg-[#D90429] transition-colors relative"
              style={{ height: `${h}%`, minHeight: "4px" }}
              title={`${p.week}: ${p.count}`}
            />
            <div className="font-mono-tech text-[8px] uppercase tracking-widest text-[#A3A3A3] mt-1 rotate-[-45deg] origin-top-left whitespace-nowrap">
              {p.week.replace(/^\d{4}-/, "")}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function TrendDetail() {
  const { tag } = useParams();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [briefingWeeks, setBriefingWeeks] = useState(4);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customTitle, setCustomTitle] = useState("");
  const [customSubtitle, setCustomSubtitle] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [subscription, setSubscription] = useState(null);
  const [subBusy, setSubBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    let tmp = null;
    try {
      let subId = subscription?.id;
      if (!subId) {
        const r = await api.post("/alerts/subscriptions", { tag });
        subId = r.data.id;
        tmp = r.data;
      }
      const r = await api.post(`/alerts/subscriptions/${subId}/preview`);
      setPreviewData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
      setPreviewOpen(false);
    } finally {
      // Guarantee cleanup of transient sub even if preview throws
      if (tmp) {
        try { await api.delete(`/alerts/subscriptions/${tmp.id}`); } catch (e) { console.warn("cleanup failed", e); }
      }
      setPreviewLoading(false);
    }
  };

  const loadSubscription = useCallback(async () => {
    try {
      const r = await api.get("/alerts/subscriptions");
      const found = (r.data || []).find((s) => s.tag === tag);
      setSubscription(found || null);
    } catch (e) {
      console.warn("alerts load failed", e);
    }
  }, [tag]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const toggleSubscription = async () => {
    setSubBusy(true);
    try {
      if (subscription) {
        await api.delete(`/alerts/subscriptions/${subscription.id}`);
        setSubscription(null);
        toast.success(lang === "es" ? "Suscripción eliminada" : "Unsubscribed");
      } else {
        const r = await api.post("/alerts/subscriptions", { tag });
        setSubscription(r.data);
        toast.success(lang === "es" ? "Suscrito a alertas semanales" : "Subscribed to weekly alerts");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSubBusy(false);
    }
  };

  const downloadBriefing = async () => {
    setBriefingLoading(true);
    try {
      const qs = new URLSearchParams({
        weeks: String(briefingWeeks),
        lang,
      });
      if (customTitle.trim()) qs.set("title", customTitle.trim());
      if (customSubtitle.trim()) qs.set("subtitle", customSubtitle.trim());
      if (customNote.trim()) qs.set("editor_note", customNote.trim());
      await downloadAuthed(
        `/briefings/actor/${encodeURIComponent(tag)}?${qs.toString()}`,
        `GalascioDAS-briefing-${tag.replace(/[^a-zA-Z0-9]/g, "-")}.pdf`
      );
      toast.success("✓");
    } catch (err) {
      toast.error(err?.message || "Error");
    } finally {
      setBriefingLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    api
      .get(`/trends/actors/${encodeURIComponent(tag)}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Error"))
      .finally(() => setLoading(false));
  }, [tag]);

  const maxMech = useMemo(() => (data?.top_mechanisms || []).reduce((m, x) => Math.max(m, x.count), 0), [data]);
  const maxObj = useMemo(() => (data?.top_objectives || []).reduce((m, x) => Math.max(m, x.count), 0), [data]);
  const maxSoph = useMemo(() => {
    if (!data) return 0;
    return Object.values(data.sophistication_distribution).reduce((m, x) => Math.max(m, x), 0);
  }, [data]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="trend-detail-page">
      <Header />
      <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-10 md:py-16 space-y-10">
        <Link
          to="/app/trends"
          data-testid="back-trends"
          className="font-mono-tech text-xs uppercase tracking-[0.3em] inline-flex items-center gap-2 hover:text-[#D90429]"
        >
          <ArrowLeft className="w-3 h-3" /> {t("trends_back")}
        </Link>

        {data && (
          <section
            data-testid="alert-bar"
            className="flex flex-wrap items-center justify-between gap-4 border border-[#171717] bg-white p-4"
          >
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-1">
                · ALERTAS · DIGEST SEMANAL
              </div>
              <div className="font-serif-display text-base leading-tight">
                {subscription
                  ? (lang === "es" ? `Suscrito como ${subscription.email}` : `Subscribed as ${subscription.email}`)
                  : (lang === "es" ? "Recibe cada semana un email con los análisis nuevos de este actor." : "Get a weekly email with new analyses for this actor.")}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="preview-email-btn"
                onClick={openPreview}
                disabled={subBusy || previewLoading}
                className="px-3 py-2 font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#525252] hover:text-[#D90429] inline-flex items-center gap-2 disabled:opacity-40"
              >
                {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                {lang === "es" ? "Vista previa" : "Preview"}
              </button>
              <button
                data-testid="toggle-subscription"
                onClick={toggleSubscription}
                disabled={subBusy}
                className={`px-6 py-3 font-mono-tech text-xs uppercase tracking-[0.3em] border-2 inline-flex items-center gap-3 disabled:opacity-40 shadow-sm ${
                  subscription
                    ? "border-[#D90429] text-[#D90429] hover:bg-[#D90429] hover:text-white"
                    : "border-[#D90429] bg-[#D90429] text-white hover:bg-[#0A0A0A] hover:border-[#0A0A0A]"
                }`}
              >
                {subBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : subscription ? (
                  <BellOff className="w-4 h-4" />
                ) : (
                  <Bell className="w-4 h-4" />
                )}
                {subscription
                  ? (lang === "es" ? "Cancelar alerta" : "Unsubscribe")
                  : (lang === "es" ? "Activar alerta semanal" : "Subscribe weekly")}
              </button>
            </div>
          </section>
        )}

        {previewOpen && (
          <div
            data-testid="preview-overlay"
            onClick={() => setPreviewOpen(false)}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-white border border-[#0A0A0A] w-full max-w-[720px] max-h-[92vh] flex flex-col"
              data-testid="preview-modal"
            >
              <div className="bg-[#0A0A0A] text-white px-5 py-4 flex items-center justify-between border-b-4 border-[#D90429]">
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429]">
                    · VISTA PREVIA · DIGEST SEMANAL
                  </div>
                  <div className="font-serif-display text-xl font-bold leading-tight">
                    #{tag}
                  </div>
                </div>
                <button
                  data-testid="preview-close"
                  onClick={() => setPreviewOpen(false)}
                  className="p-2 border border-white hover:bg-[#D90429] hover:border-[#D90429]"
                  aria-label="close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden bg-[#FAFAFA] relative">
                {previewLoading && (
                  <div className="absolute inset-0 flex items-center justify-center font-mono-tech text-xs uppercase tracking-widest text-[#A3A3A3]">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> ···
                  </div>
                )}
                {!previewLoading && previewData?.would_skip && (
                  <div className="p-8 text-center border-b border-[#E5E5E5] bg-[#FFF5F5]">
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#D90429] mb-2">
                      · POLÍTICA · SKIP-IF-ZERO
                    </div>
                    <p className="font-serif-display text-lg text-[#0A0A0A] max-w-md mx-auto">
                      {lang === "es"
                        ? "No hay análisis nuevos con esta etiqueta en los últimos 7 días. Este email NO se enviaría."
                        : "No new analyses with this tag in the last 7 days. This email would NOT be sent."}
                    </p>
                    <p className="font-mono-tech text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-3">
                      {lang === "es" ? "Estructura del digest:" : "Digest structure:"}
                    </p>
                  </div>
                )}
                {!previewLoading && previewData?.html && (
                  <iframe
                    data-testid="preview-iframe"
                    title="Email preview"
                    srcDoc={previewData.html}
                    sandbox=""
                    className="w-full h-full min-h-[480px] bg-white border-0"
                  />
                )}
              </div>
              <div className="border-t border-[#E5E5E5] px-5 py-3 flex items-center justify-between font-mono-tech text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                <span>
                  {previewData
                    ? `${previewData.count} ${lang === "es" ? "análisis en ventana" : "analyses in window"} · ${previewData.email}`
                    : "···"}
                </span>
                <span>· iframe sandboxed</span>
              </div>
            </div>
          </div>
        )}

        {data && (
          <section
            data-testid="briefing-bar"
            className="flex flex-wrap items-center justify-between gap-4 border border-[#171717] bg-white p-5"
          >
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-1">
                · REPORTE · BRIEFING PDF
              </div>
              <div className="font-serif-display text-xl leading-tight">
                {lang === "es"
                  ? "Descarga el reporte estratégico de este actor."
                  : "Download the strategic briefing for this actor."}
              </div>
            </div>
            <div className="flex items-end gap-3">
              <div>
                <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-1">
                  {lang === "es" ? "Ventana" : "Window"}
                </label>
                <div className="flex border border-[#0A0A0A]" data-testid="briefing-weeks-selector">
                  {[
                    { v: 4, label: "4 SEM" },
                    { v: 12, label: "12 SEM" },
                    { v: 0, label: lang === "es" ? "TODO" : "ALL" },
                  ].map((w) => (
                    <button
                      key={w.v}
                      data-testid={`briefing-weeks-${w.v}`}
                      onClick={() => setBriefingWeeks(w.v)}
                      className={`px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest border-r border-[#0A0A0A] last:border-r-0 ${
                        briefingWeeks === w.v ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F5F5F5]"
                      }`}
                    >
                      {w.label}
                    </button>
                  ))}
                </div>
              </div>
              <button
                data-testid="download-actor-briefing"
                onClick={downloadBriefing}
                disabled={briefingLoading}
                className="bg-[#0A0A0A] text-white px-5 py-3 font-mono-tech text-xs uppercase tracking-[0.3em] border border-[#0A0A0A] hover:bg-[#D90429] hover:border-[#D90429] disabled:opacity-40 inline-flex items-center gap-3"
              >
                {briefingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                {lang === "es" ? "Descargar PDF" : "Download PDF"}
              </button>
            </div>
            <div className="w-full">
              <button
                data-testid="toggle-customize"
                onClick={() => setCustomOpen((v) => !v)}
                className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#D90429] hover:underline mt-2"
              >
                {customOpen ? "− " : "+ "}
                {lang === "es" ? "Personalizar briefing (firma del analista)" : "Customize briefing (analyst signature)"}
              </button>
              {customOpen && (
                <div data-testid="customize-form" className="mt-3 border-t border-[#E5E5E5] pt-4 grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-1">
                      {lang === "es" ? "Título" : "Title"} <span className="text-[#404040]">({customTitle.length}/80)</span>
                    </label>
                    <input
                      data-testid="custom-title"
                      type="text"
                      maxLength={80}
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      placeholder={lang === "es" ? `Por defecto: #${tag}` : `Default: #${tag}`}
                      className="w-full border-b border-[#0A0A0A] bg-transparent px-0 py-2 text-sm focus:outline-none focus:border-[#D90429]"
                    />
                  </div>
                  <div>
                    <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-1">
                      {lang === "es" ? "Subtítulo" : "Subtitle"} <span className="text-[#404040]">({customSubtitle.length}/140)</span>
                    </label>
                    <input
                      data-testid="custom-subtitle"
                      type="text"
                      maxLength={140}
                      value={customSubtitle}
                      onChange={(e) => setCustomSubtitle(e.target.value)}
                      placeholder={lang === "es" ? "Reporte estratégico de actor" : "Strategic actor briefing"}
                      className="w-full border-b border-[#0A0A0A] bg-transparent px-0 py-2 text-sm focus:outline-none focus:border-[#D90429]"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-1">
                      {lang === "es" ? "Contexto editorial del analista (opcional)" : "Editor note (optional)"} <span className="text-[#404040]">({customNote.length}/280)</span>
                    </label>
                    <textarea
                      data-testid="custom-note"
                      maxLength={280}
                      rows={2}
                      value={customNote}
                      onChange={(e) => setCustomNote(e.target.value)}
                      placeholder={lang === "es"
                        ? "Una o dos líneas que firme el analista; aparecerán bajo el subtítulo del PDF."
                        : "One or two lines signed by the analyst; will appear under the PDF subtitle."}
                      className="w-full border border-[#E5E5E5] focus:border-[#0A0A0A] bg-white px-3 py-2 text-sm focus:outline-none resize-none font-serif-display italic"
                    />
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {loading && (
          <div className="font-mono-tech text-xs uppercase tracking-widest inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> ···
          </div>
        )}

        {error && (
          <div className="border border-[#D90429] bg-[#FFF5F5] p-6 font-mono-tech text-xs uppercase tracking-widest text-[#D90429]">
            {error}
          </div>
        )}

        {data && (
          <>
            {/* Hero */}
            <header className="grid md:grid-cols-12 gap-8 border-b border-[#171717] pb-8">
              <div className="md:col-span-7">
                <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-3">
                  · ACTOR · MONITORIZACIÓN
                </div>
                <h1 className="font-serif-display text-5xl md:text-7xl font-bold tracking-tighter leading-none break-all">
                  #{data.tag}
                </h1>
              </div>
              <div className="md:col-span-5 grid grid-cols-2 gap-4">
                <div className="bg-[#0A0A0A] text-white p-5">
                  <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-1">
                    · TOTAL
                  </div>
                  <div className="font-serif-display text-5xl font-bold leading-none">
                    {data.count}
                  </div>
                  <div className="font-mono-tech text-[10px] tracking-widest uppercase opacity-60 mt-2">
                    {t("trends_count")}
                  </div>
                </div>
                <div className="border border-[#0A0A0A] bg-white p-5">
                  <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-1">
                    · {t("trends_avg_soph")}
                  </div>
                  <div className="font-serif-display text-5xl font-bold leading-none">
                    {data.avg_sophistication_score != null ? data.avg_sophistication_score.toFixed(2) : "—"}
                  </div>
                  <div className="font-mono-tech text-[10px] tracking-widest uppercase text-[#A3A3A3] mt-2">
                    / 4.00
                  </div>
                </div>
              </div>
            </header>

            {/* Mechanisms + Objectives */}
            <section className="grid md:grid-cols-2 gap-px bg-[#171717] border border-[#171717]">
              <div className="bg-white p-6" data-testid="mechanisms-block">
                <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-2">
                  <InfoTip label="· MECANISMOS" tip={t("tip_mechanism")} testid="tip-mechanism" />
                </div>
                <h2 className="font-serif-display text-2xl font-bold mb-4 leading-tight">
                  {t("trends_top_mechanisms")}
                </h2>
                {data.top_mechanisms.length === 0 ? (
                  <div className="font-mono-tech text-xs uppercase text-[#A3A3A3]">—</div>
                ) : (
                  <div>
                    {data.top_mechanisms.map((m) => (
                      <Bar key={m.name} value={m.count} max={maxMech} label={m.name} count={m.count} accent />
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-white p-6" data-testid="objectives-block">
                <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-2">
                  <InfoTip label="· OBJETIVOS" tip={t("tip_deep_strategy")} testid="tip-deep-strategy" />
                </div>
                <h2 className="font-serif-display text-2xl font-bold mb-4 leading-tight">
                  {t("trends_top_objectives")}
                </h2>
                {data.top_objectives.length === 0 ? (
                  <div className="font-mono-tech text-xs uppercase text-[#A3A3A3]">—</div>
                ) : (
                  <div>
                    {data.top_objectives.map((o) => (
                      <Bar key={o.name} value={o.count} max={maxObj} label={o.name} count={o.count} />
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Sophistication distribution + Timeline */}
            <section className="grid md:grid-cols-2 gap-px bg-[#171717] border border-[#171717]">
              <div className="bg-white p-6" data-testid="soph-dist-block">
                <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-2">
                  <InfoTip label="· SOFISTICACIÓN" tip={t("tip_sophistication")} testid="tip-sophistication" />
                </div>
                <h2 className="font-serif-display text-2xl font-bold mb-4 leading-tight">
                  {t("trends_sophistication_dist")}
                </h2>
                <div>
                  {Object.entries(data.sophistication_distribution).map(([lvl, cnt]) => (
                    <Bar
                      key={lvl}
                      value={cnt}
                      max={maxSoph}
                      label={t(SOPH_LABEL_KEY[lvl] || lvl)}
                      count={cnt}
                      accent={lvl === "experto"}
                    />
                  ))}
                </div>
              </div>
              <div className="bg-white p-6" data-testid="timeline-block">
                <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-2">
                  · TIMELINE
                </div>
                <h2 className="font-serif-display text-2xl font-bold mb-4 leading-tight">
                  {t("trends_timeline")}
                </h2>
                <Timeline data={data.timeline} />
              </div>
            </section>

            {/* Recent analyses */}
            <section className="border border-[#171717] bg-white p-6" data-testid="recent-block">
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-2">
                · ÚLTIMOS · {data.recent_analyses.length}
              </div>
              <h2 className="font-serif-display text-2xl font-bold mb-6 leading-tight">
                {t("trends_recent")}
              </h2>
              <ul className="divide-y divide-[#E5E5E5]">
                {data.recent_analyses.map((r) => (
                  <li key={r.id} className="grid md:grid-cols-[80px_1fr_auto_auto] items-baseline gap-4 py-3" data-testid={`recent-${r.id}`}>
                    <span className="font-mono-tech text-[10px] tracking-widest text-[#A3A3A3] uppercase">
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                    <Link
                      to={`/app/analysis/${r.id}`}
                      className="font-serif-display text-lg leading-snug hover:text-[#D90429] line-clamp-2"
                    >
                      {r.title}
                    </Link>
                    <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[#404040]">
                      {r.model}
                    </span>
                    <span className={`font-mono-tech text-[10px] uppercase tracking-widest px-2 py-0.5 ${
                      r.sophistication === "experto" ? "bg-[#D90429] text-white" :
                      r.sophistication ? "bg-[#0A0A0A] text-white" : "border border-[#E5E5E5] text-[#A3A3A3]"
                    }`}>
                      {r.sophistication ? t(SOPH_LABEL_KEY[r.sophistication]) : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
